import React, { useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import {
  kycCheckDocumentsSubmitted,
  kycLoadInitPage,
  kycShowLoadingFailedScreen as kycShowLoadingFailedScreenRoutine,
} from 'spa/actions/VerificationActions';
import {
  getUserEKYCData as getUserEKYCDataRoutine,
  getUserVerificationStatuses as getUserVerificationStatusesRoutine,
} from 'spa/actions/UserActions';
import {
  VerificationDetails,
  IndividualDetailsForm,
  IndividualVerificationForm,
  CompanyDetailsForm,
  CompanyVerificationForm,
  ExtendedVerificationForm,
  HasUBOForm,
  StockExchangeForm,
  BeneficialOwnersForm,
} from 'spa/components/CustomerVerification/V2';
import {
  useGetCustomerOccupationQuery,
  useGetCustomerIndustryQuery,
} from 'spa/features/kyc/kycApi';
import {
  KYCPages,
  KYC_PAGE_HEADERS,
  KYC_DEFAULT_PAGE_HEADER,
} from 'spa/constants/VerificationConstants';
import {
  isCustomerReadySelector,
  customerKycLoadingUiSelector,
  customerKycSuccessUiSelector,
  customerKycCurrentPageSelector,
  customerKycErrorAlertSelector,
  customerEKycDataSuccess,
  isCustomerExtendedKycRequirementLoading,
  customerVerificationStatusLoading,
  customerKycSubmitLoadingSelector,
  customerVerificationStatusSuccess,
  customerKyc2RejectedSelector,
  customerKycDocumentsSubmittedIsReady,
  customerEKycDataLoading,
} from 'spa/selectors/CustomerSelectors';
import { WebappTransitionLoader } from 'spa/components/Indicators';
import { Spinner } from 'spa/components/StartTransaction';
import { CHECKOUT_FLOW, KYCContext } from 'spa/context/KYCContext';
import { Alert } from '../../../components/CustomerVerification/V2';
import { checkFromWebapp } from '../../../../utils/globals';

const KycRedirect = () => {
  const { redirect } = useContext(KYCContext);
  useEffect(() => {
    window.location.href = redirect;
  }, [redirect]);
  return (
    <div className="kyc-redirect-container">
      <KycSpinner />
      <div className="checkout-page-subheader">
        Please wait while redirect you back to your transaction. This will only take a moment.
      </div>
    </div>
  );
};

const KycPageContent = ({ page, occupationOptions, industryOptions, stockExchangeOptions }) => {
  const { redirect } = useContext(KYCContext);
  let content;
  switch (page) {
    case KYCPages.VERIFICATION_DETAILS:
      if (redirect) {
        content = <KycRedirect />;
        break;
      }
      content = <VerificationDetails />;
      break;
    case KYCPages.INDIVIDUAL_DETAILS_FORM:
      content = <IndividualDetailsForm occupationOptions={occupationOptions} />;
      break;
    case KYCPages.INDIVIDUAL_VERIFICATION_FORM:
      content = <IndividualVerificationForm />;
      break;
    case KYCPages.EXTENDED_INDIVIDUAL_VERIFICATION_FORM:
      content = <ExtendedVerificationForm />;
      break;
    case KYCPages.COMPANY_DETAILS_FORM:
      content = <CompanyDetailsForm industryOptions={industryOptions} />;
      break;
    case KYCPages.COMPANY_VERIFICATION_FORM:
      content = <CompanyVerificationForm />;
      break;
    case KYCPages.HAS_UBO_FORM:
      content = <HasUBOForm />;
      break;
    case KYCPages.STOCK_EXCHANGE_FORM:
      content = <StockExchangeForm stockExchangeOptions={stockExchangeOptions} />;
      break;
    case KYCPages.BENEFICIAL_OWNERS_FORM:
      content = <BeneficialOwnersForm />;
      break;
    default:
      content = <React.Fragment />;
  }
  return <div className="kyc-container-content">{content}</div>;
};

const KycHeader = ({ headerTitle, subheaderText }) => {
  const { flow } = useContext(KYCContext);
  const isCheckoutFlow = flow === CHECKOUT_FLOW;

  return (
    <div className={isCheckoutFlow ? 'checkout-page-kyc-headings' : 'kyc-header'}>
      <div className={isCheckoutFlow ? 'checkout-page-header' : 'kyc-header-title'}>
        {headerTitle || 'Please verify your account'}
      </div>
      <span className={isCheckoutFlow ? 'checkout-page-subheader' : 'kyc-header-title-subheader'}>
        {subheaderText}
      </span>
    </div>
  );
};

const KycSpinner = () => {
  if (checkFromWebapp()) {
    return <WebappTransitionLoader />;
  }
  return <Spinner />;
};

const V2KYC = (props) => {
  const {
    data: occupationData,
    isLoading: occupationIsLoading,
    // eslint-disable-next-line no-unused-vars
    isFetching: occupationIsFetching,
    error: occupationError,
  } = useGetCustomerOccupationQuery();

  const {
    data: industryData,
    isLoading: industryIsLoading,
    // eslint-disable-next-line no-unused-vars
    isFetching: industryIsFetching,
    error: industryError,
  } = useGetCustomerIndustryQuery();

  // Temporarily hardcoded until T336097 is pushed
  const stockExchangeData = {data: [
    { value: 1, label: 'New York Stock Exchange (NYSE)' },
    { value: 2, label: 'NASDAQ Stock Market (NASDAQ)' },
    { value: 3, label: 'NYSE American' },
    { value: 4, label: 'NYSE Arca' },
    { value: 5, label: 'NYSE National' },
    { value: 6, label: 'National Stock Exchange (NSX)' },
    { value: 7, label: 'Nasdaq PHLX / Nasdaq GEMX / Nasdaq ISE / Nasdaq MRX' },
    { value: 8, label: 'Long-Term Stock Exchange (LTSE)' },
    { value: 9, label: 'Australian Securities Exchange (ASX)' },
    { value: 10, label: 'Toronto Stock Exchange (TSX)' },
    { value: 11, label: 'TSX Venture Exchange' },
    { value: 12, label: 'Canadian Securities Exchange (CSE)' },
    { value: 13, label: 'Cboe Canada' },
    { value: 14, label: 'Montreal Exchange' },
  ]};

  const {
    getUserEKYCData,
    currentPage,
    isLoading,
    hasContactDetails,
    loadInitialPage,
    error,
    getUserVerificationStatuses,
    customerEKycIsReady,
    customerVerificationStatusIsReady,
    isCustomerKyc2Rejected,
    customerKycDoucmentsIsReady,
    checkKYCDocumentsSubmitted,
    kycShowLoadingFailedScreen,
    customerEKycLoading,
  } = props;

  const isPageLoading = isLoading || occupationIsLoading || industryIsLoading;

  const { title: headerTitle, subheader: subheaderText } =
    KYC_PAGE_HEADERS[currentPage] || KYC_DEFAULT_PAGE_HEADER;

  useEffect(() => {
    !customerEKycIsReady && !customerEKycLoading && getUserEKYCData();
    !customerVerificationStatusIsReady && getUserVerificationStatuses();
    !customerKycDoucmentsIsReady && checkKYCDocumentsSubmitted();
  }, [
    getUserEKYCData,
    getUserVerificationStatuses,
    customerEKycIsReady,
    customerVerificationStatusIsReady,
    customerKycDoucmentsIsReady,
    checkKYCDocumentsSubmitted,
    customerEKycLoading,
  ]);

  const kycContext = useContext(KYCContext);

  useEffect(() => {
    const isInitialView = !currentPage && !isLoading;
    if (isInitialView) {
      loadInitialPage({
        kycContext,
      });
    }
  }, [
    currentPage,
    isLoading,
    hasContactDetails,
    loadInitialPage,
    kycContext,
    isCustomerKyc2Rejected,
  ]);

  useEffect(() => {
    if (occupationError || industryError) {
      kycShowLoadingFailedScreen();
    }
  }, [occupationError, industryError, kycShowLoadingFailedScreen, isLoading]);

  return (
    <div className="kyc kyc-container">
      {!isLoading && error && (
        <div className="kyc-alert">
          <Alert {...error} type={'error'} />{' '}
        </div>
      )}
      <KycHeader headerTitle={headerTitle} subheaderText={subheaderText} />
      {isPageLoading && <KycSpinner />}
      {!isPageLoading && !occupationError && !industryError && (
        <KycPageContent
          page={currentPage}
          occupationOptions={occupationData.data}
          industryOptions={industryData.data}
          stockExchangeOptions={stockExchangeData.data}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isLoading:
    !customerKycSubmitLoadingSelector(state) &&
    (!isCustomerReadySelector(state) ||
      customerKycLoadingUiSelector(state) ||
      customerVerificationStatusLoading(state) ||
      isCustomerExtendedKycRequirementLoading(state) ||
      !customerEKycDataSuccess(state)),
  isCustomerKyc2Rejected: customerKyc2RejectedSelector(state),
  isNextPageLoadSucess: customerKycSuccessUiSelector(state),
  currentPage: customerKycCurrentPageSelector(state),
  error: customerKycErrorAlertSelector(state),
  customerEKycIsReady: customerEKycDataSuccess(state),
  customerVerificationStatusIsReady: customerVerificationStatusSuccess(state),
  customerKycDoucmentsIsReady: customerKycDocumentsSubmittedIsReady(state),
  customerEKycLoading: customerEKycDataLoading(state),
});

const mapDispatchToProps = (dispatch) => ({
  loadInitialPage: (payload) => dispatch(kycLoadInitPage(payload)),
  getUserEKYCData: () => dispatch(getUserEKYCDataRoutine({ getFields: true })),
  getUserVerificationStatuses: () => dispatch(getUserVerificationStatusesRoutine()),
  checkKYCDocumentsSubmitted: () => dispatch(kycCheckDocumentsSubmitted()),
  kycShowLoadingFailedScreen: () => dispatch(kycShowLoadingFailedScreenRoutine()),
});

export default connect(mapStateToProps, mapDispatchToProps)(V2KYC);
